import React, { useEffect, useRef, useState } from "react";
import { ArrowRepeat } from "react-bootstrap-icons";
import BpmnJS from "bpmn-js/lib/Modeler";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Alert } from "../components/ui/Alert";
import { Field, Input, Switch } from "../components/ui/Field";
import { useDialogs } from "../components/ui/dialogs";
import { cn } from "../lib/cn";

// bpmn-js stylesheets are imported once in src/index.tsx so that index.css,
// which restyles the .djs-* chrome, always loads after them.

const API_URL = import.meta.env.VITE_API_URL || "";

type AzDoSettings = {
  enabled?: boolean;
  organization?: string;
  project?: string;
  personalAccessToken?: string;
  repositoryName?: string;
  wikiName?: string;
  templateRepoName?: string;
  templatePath?: string;
  syncSchedule?: string;
  wikiBranch?: string;
  repositoryBranch?: string;
  boardAreaPath?: string;
};

type SyncItem = {
  processName: string;
  wikiPath: string;
  xml: string;
  metadata: any;
  activities: any[];
  parsedDocuments?: string[];
  skipReason?: string;
};

type LogLevel = "success" | "warning" | "error";
type LogEntry = { level: LogLevel; message: string };

const LOG_TONE: Record<LogLevel, string> = {
  success: "text-success",
  warning: "text-warning",
  error: "text-danger",
};

export default function AzDoSettingsPage({ token }: { token: string }) {
  const dialogs = useDialogs();

  const [settings, setSettings] = useState<AzDoSettings>({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [syncing, setSyncing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMsg, setStatusMsg] = useState("");
  const [logs, setLogs] = useState<LogEntry[]>([]);

  // Off-screen modeler used to rasterise each diagram to SVG in the browser.
  const hiddenContainerRef = useRef<HTMLDivElement>(null);
  const modelerRef = useRef<any>(null);
  const logEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/azdo-settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setSettings(await res.json());
      } catch (err) {
        console.error("Failed to fetch AzDO settings", err);
      }
    };
    fetchSettings();

    if (!modelerRef.current && hiddenContainerRef.current) {
      modelerRef.current = new BpmnJS({ container: hiddenContainerRef.current });
    }

    return () => {
      if (modelerRef.current) {
        modelerRef.current.destroy();
        modelerRef.current = null;
      }
    };
  }, [token]);

  // Keep the newest log line in view during a long sync.
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ block: "nearest" });
  }, [logs]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);
    try {
      await fetch(`${API_URL}/api/admin/azdo-settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(settings),
      });
      setSuccess(true);
    } catch {
      await dialogs.notify({
        title: "Could not save settings",
        description: "The request to the server failed. Please try again.",
        tone: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  const addLog = (level: LogLevel, message: string) =>
    setLogs((prev) => [...prev, { level, message }]);

  const handleSync = async () => {
    const confirmed = await dialogs.confirm({
      title: "Run a full wiki sync?",
      description:
        "Every approved diagram will be rendered in your browser and its Azure DevOps wiki page overwritten.",
      confirmLabel: "Start sync",
    });
    if (!confirmed) return;

    setSyncing(true);
    setLogs([]);
    setProgress(0);
    setStatusMsg("Fetching sync plan…");

    try {
      const planRes = await fetch(`${API_URL}/api/admin/azdo-sync-plan`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { plan } = await planRes.json();

      if (!plan || plan.length === 0) {
        addLog("warning", "No diagrams found to sync.");
        setStatusMsg("Nothing to do.");
        setSyncing(false);
        return;
      }

      addLog("success", `Plan received — ${plan.length} item(s).`);

      // Pages actually written this run. Only these count as "still live" when
      // deciding what has gone stale.
      const activeWikiPaths: string[] = [];
      // Pages whose publish threw. Deliberately kept apart from both lists: a
      // process that was deleted or had its approval revoked never reaches the
      // plan at all, and one skipped for invalid metadata is genuinely stale --
      // but "Azure DevOps returned 503" is not evidence of either, so these are
      // left exactly as they are for the next run.
      const unjudgedWikiPaths: string[] = [];

      let processed = 0;
      const total = plan.length;

      for (const item of plan as SyncItem[]) {
        processed += 1;
        setProgress(Math.round((processed / total) * 100));
        setStatusMsg(`Processing ${processed} of ${total}: ${item.processName}`);

        if (item.skipReason) {
          addLog("warning", `Skipped ${item.processName}: ${item.skipReason}`);
          continue;
        }

        try {
          await modelerRef.current.importXML(item.xml);

          // Fit the viewport before exporting so the SVG bounds are correct.
          modelerRef.current.get("canvas").zoom("fit-viewport");
          const { svg } = await modelerRef.current.saveSVG();

          const syncRes = await fetch(`${API_URL}/api/admin/azdo-sync-item`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              wikiPath: item.wikiPath,
              processName: item.processName,
              svgBase64: svg,
              metadata: item.metadata,
              activities: item.activities,
              parsedDocuments: item.parsedDocuments,
            }),
          });

          if (!syncRes.ok) {
            const errText = await syncRes.text();

            // The throw has to happen outside the try: it used to sit inside,
            // where its own catch swallowed it and reported "invalid response"
            // for every failure, including ones the server had explained.
            let message = `Server returned ${syncRes.status}.`;
            try {
              const parsed = JSON.parse(errText);
              if (parsed?.error) {
                message = parsed.error;
                if (parsed.status) message += ` (Azure DevOps returned ${parsed.status}.)`;
                if (parsed.wikiPathLength > 200) {
                  message +=
                    ` The wiki path is ${parsed.wikiPathLength} characters;` +
                    " Azure DevOps caps the fully qualified path at 235, including" +
                    " the repository URL. Shortening a process name may fix it.";
                }
              }
            } catch {
              if (errText.trim()) message = errText.slice(0, 200);
            }

            throw new Error(message);
          }

          activeWikiPaths.push(item.wikiPath);
          addLog("success", `Synced ${item.wikiPath}`);
        } catch (err: any) {
          unjudgedWikiPaths.push(item.wikiPath);
          addLog("error", `Failed ${item.processName}: ${err.message}`);
        }
      }

      // The board runs once, after every wiki page exists, because work items
      // link to their parents and their descriptions link to the wiki.
      // Flag pages whose diagram has gone. Runs after the loop so the active
      // list is complete, and before the board pass so a failure here is still
      // reported separately rather than being mistaken for a board problem.
      setStatusMsg("Checking for stale wiki pages…");
      try {
        const staleRes = await fetch(`${API_URL}/api/admin/azdo-sync-stale`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ activeWikiPaths, unjudgedWikiPaths }),
        });

        const stale = await staleRes.json();
        if (!staleRes.ok) throw new Error(stale.error || "Stale check failed.");

        if (stale.skipped) {
          addLog("warning", `Stale check skipped: ${stale.reason}`);
        } else {
          // The counts and the all-clear are reassurance, not warnings; only an
          // actual banner or failure deserves the warning styling.
          (stale.logs || []).forEach((line: string) =>
            addLog(line.startsWith("Stale check") ? "success" : "warning", line),
          );
        }
      } catch (err: any) {
        addLog("error", `Stale check failed: ${err.message}`);
      }

      setStatusMsg("Updating the Azure DevOps board…");
      try {
        const boardRes = await fetch(`${API_URL}/api/admin/azdo-sync-board`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          // Only the fields the board needs; the XML and activity lists would
          // make this payload many times larger for no benefit.
          body: JSON.stringify({
            plan: (plan as SyncItem[])
              .filter((item) => !item.skipReason)
              .map(({ processName, wikiPath, metadata }) => ({
                processName,
                wikiPath,
                metadata,
              })),
          }),
        });

        const board = await boardRes.json();
        if (!boardRes.ok) throw new Error(board.error || "Board synchronisation failed.");

        if (board.skipped) {
          addLog("warning", `Board skipped: ${board.reason}`);
        } else {
          (board.logs || []).forEach((entry: { level: LogLevel; message: string }) =>
            addLog(entry.level, `Board: ${entry.message}`),
          );
          addLog(
            board.failed > 0 ? "warning" : "success",
            `Board: ${board.created} created, ${board.updated} updated, ` +
              `${board.stale} stale, ${board.failed} failed.`,
          );
        }
      } catch (err: any) {
        addLog("error", `Board synchronisation failed: ${err.message}`);
      }

      setStatusMsg("Sync complete.");
    } catch {
      addLog("error", "Global sync error: the request failed.");
      setStatusMsg("Sync failed.");
    } finally {
      setSyncing(false);
    }
  };

  const set = <K extends keyof AzDoSettings>(key: K, value: AzDoSettings[K]) =>
    setSettings((prev) => ({ ...prev, [key]: value }));

  const textField =
    (key: keyof AzDoSettings, placeholder?: string) =>
    (props: { id: string; "aria-describedby"?: string }) => (
      <Input
        {...props}
        value={(settings[key] as string) || ""}
        onChange={(e) => set(key, e.target.value)}
        placeholder={placeholder}
      />
    );

  const failureCount = logs.filter((l) => l.level === "error").length;

  return (
    <div className="p-5">
      <header className="mb-4">
        <h1 className="text-ink text-lg font-semibold tracking-tight">
          Azure DevOps sync
        </h1>
        <p className="text-ink-muted mt-0.5 text-[13px]">
          Publish approved diagrams to an Azure DevOps wiki.
        </p>
      </header>

      {/* Kept off-screen rather than display:none so bpmn-js can still measure
          the container when generating SVGs. */}
      <div
        ref={hiddenContainerRef}
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "-10000px",
          top: 0,
          width: "1024px",
          height: "768px",
          visibility: "hidden",
        }}
      />

      <div className="grid items-start gap-5 xl:grid-cols-[1fr_360px]">
        <Card title="Configuration">
          <form onSubmit={handleSave} className="space-y-5">
            {success && <Alert tone="success">Settings saved.</Alert>}

            <Switch
              checked={settings.enabled || false}
              onChange={(checked) => set("enabled", checked)}
              label="Enable wiki synchronization"
              hint="Required before a manual or scheduled sync will run."
            />

            <div className="border-line space-y-4 border-t pt-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Organization">
                  {textField("organization", "my-org")}
                </Field>
                <Field label="Project">{textField("project", "MyProject")}</Field>
              </div>

              <Field label="Personal access token">
                {(props) => (
                  <Input
                    {...props}
                    type="password"
                    value={settings.personalAccessToken || ""}
                    onChange={(e) => set("personalAccessToken", e.target.value)}
                    autoComplete="off"
                  />
                )}
              </Field>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Wiki name">
                  {textField("wikiName", "MyProject.wiki")}
                </Field>
                <Field label="Images repository">
                  {textField("repositoryName", "diagrams-repo")}
                </Field>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Template repository">
                  {textField("templateRepoName", "templates-repo")}
                </Field>
                <Field label="Template path">
                  {textField("templatePath", "/templates/process.md")}
                </Field>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Wiki branch" hint="Usually wikiMain for project wikis.">
                  {textField("wikiBranch", "wikiMain")}
                </Field>
                <Field label="Repository branch" hint="Target branch for standard Git repos.">
                  {textField("repositoryBranch", "main")}
                </Field>
              </div>

              <Field
                label="Board area path"
                hint="Where process work items are filed. Leave blank for the project root. Scoping them lets the access token's identity be granted “Edit work items in this node” on one branch instead of the whole project."
              >
                {textField("boardAreaPath", "MyProject\\Processes")}
              </Field>

              <Field
                label="Sync schedule"
                hint="Cron format: minute hour day month weekday. “0 2 * * *” runs daily at 02:00."
              >
                {(props) => (
                  <Input
                    {...props}
                    value={settings.syncSchedule || "0 2 * * *"}
                    onChange={(e) => set("syncSchedule", e.target.value)}
                    placeholder="0 2 * * *"
                    className="font-mono text-xs"
                  />
                )}
              </Field>
            </div>

            <Button type="submit" size="md" variant="primary" disabled={loading}>
              {loading && (
                <span
                  aria-hidden="true"
                  className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/35 border-t-white"
                />
              )}
              {loading ? "Saving" : "Save settings"}
            </Button>
          </form>
        </Card>

        <Card
          title="Manual sync"
          description="Diagrams are rendered to SVG in your browser, then uploaded."
        >
          <div className="space-y-3.5">
            <Button
              size="md"
              variant="primary"
              className="w-full"
              onClick={handleSync}
              disabled={syncing || !settings.enabled}
              title={
                !settings.enabled
                  ? "Enable wiki synchronization first"
                  : "Run a full sync now"
              }
            >
              <ArrowRepeat size={15} className={cn(syncing && "animate-spin")} />
              {syncing ? "Syncing…" : "Sync now"}
            </Button>

            {!settings.enabled && (
              <p className="text-ink-subtle text-xs leading-relaxed">
                Turn on wiki synchronization and save before running a sync.
              </p>
            )}

            {(syncing || progress > 0) && (
              <div className="animate-fade-in space-y-1.5">
                <div
                  role="progressbar"
                  aria-valuenow={progress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label="Sync progress"
                  className="bg-surface-sunken h-1.5 w-full overflow-hidden rounded-full"
                >
                  <div
                    className="bg-accent h-full rounded-full transition-[width] duration-(--duration-base) ease-(--ease-standard)"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="text-ink-subtle flex items-baseline justify-between gap-2 text-xs">
                  <span className="min-w-0 truncate">{statusMsg}</span>
                  <span className="shrink-0 tabular-nums">{progress}%</span>
                </div>
              </div>
            )}

            {logs.length > 0 && (
              <div className="animate-fade-in">
                <div className="mb-1.5 flex items-baseline justify-between">
                  <h3 className="text-ink-muted text-xs font-semibold tracking-wide uppercase">
                    Log
                  </h3>
                  {failureCount > 0 && (
                    <span className="text-danger text-xs font-semibold tabular-nums">
                      {failureCount} failed
                    </span>
                  )}
                </div>
                <div
                  role="log"
                  aria-live="polite"
                  className="border-line bg-surface-sunken max-h-56 space-y-0.5 overflow-y-auto rounded-lg border p-2.5 font-mono text-[11px] leading-relaxed"
                >
                  {logs.map((entry, index) => (
                    <p key={index} className={cn("break-words", LOG_TONE[entry.level])}>
                      {entry.message}
                    </p>
                  ))}
                  <div ref={logEndRef} />
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
